import { useState } from 'react'
import { useTranslation } from '../i18n'
import { apiPut } from '../hooks/useApi'
import type { Settings } from '../types'

interface FTESettingsProps {
  settings: Settings | null
  onUpdate: () => void
}

export function FTESettings({ settings, onUpdate }: FTESettingsProps) {
  const { t } = useTranslation()
  const [fteCount, setFteCount] = useState('')
  const [saving, setSaving] = useState(false)
  const [syncedFteCount, setSyncedFteCount] = useState(settings?.fteCount)

  if (settings?.fteCount !== syncedFteCount) {
    setSyncedFteCount(settings?.fteCount)
    if (settings?.fteCount !== undefined) {
      setFteCount(settings.fteCount.toString())
    }
  }

  const isLoading = settings === null
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const value = parseFloat(fteCount)
      if (isNaN(value) || value <= 0) {
        throw new Error(t('error.fteMustBePositive'))
      }

      await apiPut<Settings>('/settings', { fteCount: value })
      onUpdate()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToUpdateSettings'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card" data-testid="fte-settings">
      <h3>{t('component.fteSettings')}</h3>
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
      >
        <label htmlFor="fteCount" style={{ fontWeight: 500, color: 'var(--color-text)' }}>
          {t('label.fteCount')}
        </label>
        <input
          id="fteCount"
          type="number"
          step="0.5"
          min="0.5"
          value={fteCount}
          onChange={(e) => setFteCount(e.target.value)}
          disabled={isLoading}
          placeholder={isLoading ? t('label.loading') : ''}
          className="input"
          style={{ width: '100px' }}
          data-testid="fte-input"
        />
        <button type="submit" disabled={saving || isLoading} data-testid="fte-save">
          {saving ? t('misc.saving') : t('button.save')}
        </button>
      </form>
      {error && <p style={{ color: 'var(--color-error)', marginTop: '0.5rem' }}>{error}</p>}
      <p
        style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}
      >
        {t('misc.fteHelp')}
      </p>
    </div>
  )
}
